import { ChevronDown, LogOut, Menu, Moon, Plus, Search, Sun, User as UserIcon } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../features/auth/AuthProvider';
import { useTheme } from '../../features/theme/ThemeProvider';
import { crmApi } from '../../lib/api';
import { Sidebar } from './Sidebar';
import { Softphone } from '../../features/phone/Softphone';

const ROUTE_LABELS: Record<string, string> = {
	today: 'Today',
	inbox: 'Inbox',
	leads: 'Leads',
	pipelines: 'Pipelines',
	companies: 'Companies',
	contacts: 'Contacts',
	activities: 'Activity',
	emails: 'Email',
	calls: 'Call list',
	dialer: 'Power dialer',
	campaigns: 'Campaigns',
	map: 'Territory',
	admin: 'Admin',
	system: 'System',
	settings: 'Settings',
	profile: 'My profile'
};

function Breadcrumbs() {
	const { pathname } = useLocation();
	const parts = pathname.split('/').filter(Boolean);
	const crumbs = parts.map((p) => ROUTE_LABELS[p] ?? p.charAt(0).toUpperCase() + p.slice(1));
	if (crumbs.length === 0) return <span className="text-sm font-medium text-primary">Today</span>;
	return (
		<nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb">
			{crumbs.map((c, i) => (
				<span key={i} className="flex items-center gap-1">
					{i > 0 ? <span className="text-muted">/</span> : null}
					<span className={i === crumbs.length - 1 ? 'font-medium text-primary' : 'text-muted'}>{c}</span>
				</span>
			))}
		</nav>
	);
}

function ThemeToggle() {
	const { theme, toggle } = useTheme();
	return (
		<button
			type="button"
			onClick={toggle}
			aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
			className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-subtle bg-surface text-secondary transition hover:bg-surface-hover hover:text-primary"
		>
			{theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
		</button>
	);
}

function UserMenu() {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		function onDoc(e: MouseEvent) {
			if (!ref.current?.contains(e.target as Node)) setOpen(false);
		}
		if (open) document.addEventListener('mousedown', onDoc);
		return () => document.removeEventListener('mousedown', onDoc);
	}, [open]);
	const initials = useMemo(() => {
		const label = user?.name || user?.email || 'GC';
		return label.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
	}, [user]);
	return (
		<div className="relative" ref={ref}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="inline-flex items-center gap-2 rounded-md border border-subtle bg-surface py-1 pl-1 pr-2 text-sm text-secondary transition hover:bg-surface-hover"
			>
				<span className="flex h-6 w-6 items-center justify-center rounded bg-accent text-[10px] font-semibold text-accent-contrast">{initials}</span>
				<span className="hidden text-primary md:inline">{user?.name?.split(' ')[0] || 'User'}</span>
				<ChevronDown className="h-3.5 w-3.5 text-muted" />
			</button>
			{open ? (
				<div className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-lg border border-subtle bg-surface shadow-pop">
					<div className="border-b border-subtle px-3 py-2">
						<p className="truncate text-sm font-medium text-primary">{user?.name || 'GalloCRM User'}</p>
						<p className="truncate text-xs text-muted">{user?.email}</p>
					</div>
					<button
						onClick={() => { setOpen(false); navigate('/profile'); }}
						className="flex w-full items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-surface-hover hover:text-primary"
					>
						<UserIcon className="h-3.5 w-3.5" /> My profile
					</button>
					<button
						onClick={async () => { setOpen(false); await logout(); navigate('/auth/login'); }}
						className="flex w-full items-center gap-2 border-t border-subtle px-3 py-2 text-sm text-secondary hover:bg-surface-hover hover:text-primary"
					>
						<LogOut className="h-3.5 w-3.5" /> Sign out
					</button>
				</div>
			) : null}
		</div>
	);
}

function QuickAddModal({ open, onClose }: { open: boolean; onClose: () => void }) {
	const queryClient = useQueryClient();
	const [type, setType] = useState<'company' | 'contact' | 'lead' | 'opportunity'>('company');
	const [form, setForm] = useState<Record<string, string>>({});
	const companiesQuery = useQuery({ queryKey: ['companies', 'quick-add'], queryFn: () => crmApi.companies(), enabled: open });
	const mut = useMutation({
		mutationFn: async () => {
			if (type === 'company') return crmApi.createCompany({ name: (form.name || '').trim(), website_domain: form.website_domain?.trim() || null });
			if (type === 'contact' || type === 'lead') {
				return crmApi.createContact({
					first_name: (form.first_name || '').trim(),
					last_name: (form.last_name || '').trim(),
					email: (form.email || '').trim(),
					phone: form.phone?.trim() || null,
					title: form.title?.trim() || null,
					company_id: form.company_id ? Number(form.company_id) : null,
					lead_status: type === 'lead' ? (form.lead_status || 'new') : null
				});
			}
			return crmApi.createOpportunity({
				title: (form.title || '').trim(),
				company_id: Number(form.company_id),
				stage: form.stage || 'New'
			});
		},
		onSuccess: async () => {
			toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} created`);
			setForm({});
			onClose();
			await queryClient.invalidateQueries({ queryKey: ['companies'] });
			await queryClient.invalidateQueries({ queryKey: ['contacts'] });
			await queryClient.invalidateQueries({ queryKey: ['opportunities'] });
			await queryClient.invalidateQueries({ queryKey: ['leads'] });
		},
		onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed')
	});

	if (!open) return null;
	const companies = Array.isArray(companiesQuery.data) ? companiesQuery.data : [];

	return (
		<div className="fixed inset-0 z-[60] flex items-start justify-center bg-canvas/60 p-4 pt-24 backdrop-blur-sm" onClick={onClose}>
			<div className="w-full max-w-lg overflow-hidden rounded-xl border border-subtle bg-surface shadow-pop" onClick={(e) => e.stopPropagation()}>
				<div className="border-b border-subtle px-4 py-3">
					<h2 className="text-sm font-semibold text-primary">Quick create</h2>
					<p className="text-xs text-muted">Create a record without leaving this page.</p>
				</div>
				<div className="flex gap-1 border-b border-subtle px-3 py-2">
					{(['company', 'contact', 'lead', 'opportunity'] as const).map((t) => (
						<button
							key={t}
							onClick={() => setType(t)}
							className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${type === t ? 'bg-accent/10 text-accent' : 'text-secondary hover:bg-surface-hover'}`}
						>
							{t.charAt(0).toUpperCase() + t.slice(1)}
						</button>
					))}
				</div>
				<div className="space-y-2 p-4">
					{type === 'company' ? (
						<>
							<input className="input" placeholder="Company name" value={form.name || ''} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} />
							<input className="input" placeholder="Website (optional)" value={form.website_domain || ''} onChange={(e) => setForm((c) => ({ ...c, website_domain: e.target.value }))} />
						</>
					) : null}
					{type === 'contact' || type === 'lead' ? (
						<>
							<div className="grid grid-cols-2 gap-2">
								<input className="input" placeholder="First name" value={form.first_name || ''} onChange={(e) => setForm((c) => ({ ...c, first_name: e.target.value }))} />
								<input className="input" placeholder="Last name" value={form.last_name || ''} onChange={(e) => setForm((c) => ({ ...c, last_name: e.target.value }))} />
							</div>
							<input className="input" placeholder="Email" value={form.email || ''} onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))} />
							<input className="input" placeholder="Phone (optional)" value={form.phone || ''} onChange={(e) => setForm((c) => ({ ...c, phone: e.target.value }))} />
							<select className="input" value={form.company_id || ''} onChange={(e) => setForm((c) => ({ ...c, company_id: e.target.value }))}>
								<option value="">No company</option>
								{companies.map((co) => <option key={String(co.id)} value={String(co.id)}>{String(co.name ?? co.id)}</option>)}
							</select>
						</>
					) : null}
					{type === 'opportunity' ? (
						<>
							<input className="input" placeholder="Opportunity title" value={form.title || ''} onChange={(e) => setForm((c) => ({ ...c, title: e.target.value }))} />
							<select className="input" value={form.company_id || ''} onChange={(e) => setForm((c) => ({ ...c, company_id: e.target.value }))}>
								<option value="">Select company</option>
								{companies.map((co) => <option key={String(co.id)} value={String(co.id)}>{String(co.name ?? co.id)}</option>)}
							</select>
						</>
					) : null}
				</div>
				<div className="flex items-center justify-end gap-2 border-t border-subtle px-4 py-3">
					<button onClick={onClose} className="btn-secondary">Cancel</button>
					<button onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
						{mut.isPending ? 'Creating…' : 'Create'}
					</button>
				</div>
			</div>
		</div>
	);
}

export function AppShell() {
	const [mobileOpen, setMobileOpen] = useState(false);
	const [quickAddOpen, setQuickAddOpen] = useState(false);

	return (
		<div className="min-h-screen bg-canvas text-primary">
			<div className="flex min-h-screen">
				<Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
				<div className="flex min-h-screen flex-1 flex-col lg:pl-60">
					<header className="sticky top-0 z-30 border-b border-subtle bg-surface/80 backdrop-blur-md">
						<div className="flex items-center gap-2 px-4 py-2">
							<button
								type="button"
								onClick={() => setMobileOpen(true)}
								className="btn-ghost p-1.5 lg:hidden"
							>
								<Menu className="h-4 w-4" />
							</button>
							<div className="flex-1">
								<Breadcrumbs />
							</div>
							<div className="hidden items-center gap-1.5 rounded-md border border-subtle bg-surface-2 px-2.5 py-1.5 text-sm text-muted md:flex md:min-w-[280px]">
								<Search className="h-3.5 w-3.5" />
								<input placeholder="Search" className="w-full border-none bg-transparent p-0 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-0" />
								<span className="kbd">⌘K</span>
							</div>
							<button onClick={() => setQuickAddOpen(true)} className="btn-primary">
								<Plus className="h-3.5 w-3.5" />
								<span className="hidden sm:inline">Create</span>
							</button>
							<ThemeToggle />
							<UserMenu />
						</div>
					</header>
					<main className="flex-1 px-4 py-5 lg:px-6 lg:py-6">
						<Outlet />
					</main>
				</div>
			</div>
			<QuickAddModal open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
			<Softphone />
		</div>
	);
}
